/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: permissions entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the permissions table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the permissions API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, perm_name, description, created_at]
 * Searchable fields: []
 * Filterable fields: [id]
 */

import { httpClient } from "../http-client.js";
import type { UnifiedQueryOptions } from "../../../../../config/search/global-search-config.js";
import type {
  PermissionsRow,
  PermissionsInsert,
  PermissionsUpdate,
} from "../../types/generated_schemas/generated-permissions-schema.js";

export interface PermissionsQueryOptions extends UnifiedQueryOptions {}

export interface PermissionsResponse {
  data: PermissionsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for permissions table operations
 *
 * @description Provides type-safe access to permissions API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class PermissionsClient {
  private readonly basePath = "/permissions";

  /**
   * Get all permissions records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of permissions records
   */
  async getAll(options?: PermissionsQueryOptions): Promise<PermissionsRow[]> {
    const params = new URLSearchParams();

    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.offset) params.set("offset", options.offset.toString());
    if (options?.sortBy) params.set("sortBy", options.sortBy);
    if (options?.sortOrder) params.set("sortOrder", options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    const response = await httpClient.get<PermissionsResponse>(url);
    return response.data;
  }

  /**
   * Get a single permissions record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the permissions record
   */
  async getById(id: string): Promise<PermissionsRow> {
    const response = await httpClient.get<{
      data: PermissionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new permissions record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created permissions record
   */
  async create(data: PermissionsInsert): Promise<PermissionsRow> {
    const response = await httpClient.post<{
      data: PermissionsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing permissions record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated permissions record
   */
  async update(id: string, data: PermissionsUpdate): Promise<PermissionsRow> {
    const response = await httpClient.put<{
      data: PermissionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a permissions record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple permissions records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created permissions records
   */
  async createMany(data: PermissionsInsert[]): Promise<PermissionsRow[]> {
    const response = await httpClient.post<{
      data: PermissionsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple permissions records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search permissions with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: PermissionsQueryOptions,
  ): Promise<PermissionsResponse> {
    throw new Error(
      "Search operations are not available for permissions - no searchable fields configured",
    );
  }

  /**
   * Get count of permissions records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString
      ? `${this.basePath}/count?${queryString}`
      : `${this.basePath}/count`;

    const response = await httpClient.get<{ data: number; success: boolean }>(
      url,
    );
    return response.data;
  }

  /**
   * Check if a permissions record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: "permissions",
      publicFields: ["id", "perm_name", "description", "created_at"],
      searchableFields: [],
      filterableFields: ["id"],
      sortableFields: ["created_at"],
      readonlyFields: ["id", "created_at"],
      requiredFields: ["id", "perm_name"],
    };
  }
}

// Export singleton instance for convenience
export const permissionsClient = new PermissionsClient();
